"use client";

import { useEffect, useState } from "react";
import type { RefObject } from "react";

const LANDING_DEFERRED_SECTION_THRESHOLD = 0.01;

interface LandingDeferredSectionVisibility {
  hasEnteredView: boolean;
  isInView: boolean;
}

interface UseLandingDeferredSectionVisibilityInput {
  containerRef: RefObject<HTMLElement | null> | null | undefined;
  sectionRef: RefObject<HTMLElement | null>;
}

interface StartLandingDeferredSectionObserverInput {
  containerRef: RefObject<HTMLElement | null> | null | undefined;
  sectionRef: RefObject<HTMLElement | null>;
  setHasEnteredView: (value: boolean) => void;
  setIsInView: (value: boolean) => void;
}

interface ApplyIntersectionEntryInput {
  entry: IntersectionObserverEntry | undefined;
  setHasEnteredView: (value: boolean) => void;
  setIsInView: (value: boolean) => void;
}

function applyIntersectionEntry({
  entry,
  setHasEnteredView,
  setIsInView,
}: ApplyIntersectionEntryInput): void {
  const nextIsInView = entry?.isIntersecting ?? false;
  setIsInView(nextIsInView);

  if (nextIsInView) {
    setHasEnteredView(true);
  }
}

function startIntersectionObserverFallback({
  setHasEnteredView,
  setIsInView,
}: Omit<ApplyIntersectionEntryInput, "entry">): VoidFunction {
  const fallbackFrameId = window.requestAnimationFrame(() => {
    setIsInView(true);
    setHasEnteredView(true);
  });

  return () => window.cancelAnimationFrame(fallbackFrameId);
}

function startIntersectionObserver({
  containerRef,
  section,
  setHasEnteredView,
  setIsInView,
}: Omit<StartLandingDeferredSectionObserverInput, "sectionRef"> & {
  section: HTMLElement;
}): VoidFunction {
  const observer = new IntersectionObserver(
    ([entry]) => {
      applyIntersectionEntry({ entry, setHasEnteredView, setIsInView });
    },
    {
      root: containerRef?.current ?? null,
      threshold: LANDING_DEFERRED_SECTION_THRESHOLD,
    },
  );

  observer.observe(section);
  return () => observer.disconnect();
}

function startVisibleFallbackOrObserver(
  input: Omit<StartLandingDeferredSectionObserverInput, "sectionRef"> & {
    section: HTMLElement;
  },
): VoidFunction {
  if (typeof IntersectionObserver === "undefined") {
    return startIntersectionObserverFallback({
      setHasEnteredView: input.setHasEnteredView,
      setIsInView: input.setIsInView,
    });
  }

  return startIntersectionObserver(input);
}

function startLandingDeferredSectionObserver({
  containerRef,
  sectionRef,
  setHasEnteredView,
  setIsInView,
}: StartLandingDeferredSectionObserverInput): VoidFunction | undefined {
  const section = sectionRef.current;
  if (!section) {
    return undefined;
  }

  return startVisibleFallbackOrObserver({
    containerRef,
    section,
    setHasEnteredView,
    setIsInView,
  });
}

export function useLandingDeferredSectionVisibility({
  containerRef,
  sectionRef,
}: UseLandingDeferredSectionVisibilityInput): LandingDeferredSectionVisibility {
  const [isInView, setIsInView] = useState(false);
  const [hasEnteredView, setHasEnteredView] = useState(false);

  useEffect(() => {
    return startLandingDeferredSectionObserver({
      containerRef,
      sectionRef,
      setHasEnteredView,
      setIsInView,
    });
  }, [containerRef, sectionRef]);

  return {
    hasEnteredView,
    isInView,
  };
}
